import React, { useState } from "react";
import { toast } from "react-toastify";
import { addStudyToModel } from "../../services/aiModels";
import "../datasets/Datasets.css";

const AssignToolToStudyModal = ({ tool, studies, onClose, onSuccess }) => {
    const [selectedStudies, setSelectedStudies] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleCheckboxChange = (studyId) => {
        setSelectedStudies((prev) => {
            if (prev.includes(studyId)) {
                return prev.filter((id) => id !== studyId);
            } else {
                return [...prev, studyId];
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedStudies.length === 0) {
            toast.warning("Please select at least one study");
            return;
        }

        setLoading(true);
        try {
            const promises = selectedStudies.map((studyId) =>
                addStudyToModel(tool.id, studyId)
            );

            await Promise.all(promises);
            toast.success(`Tool assigned to ${selectedStudies.length} study(ies)`);
            onSuccess();
        } catch (err) {
            console.error("Error assigning tool to studies:", err);
            toast.error("Failed to assign tool to some studies.");
        } finally {
            setLoading(false);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const getStatusBadge = (status) => {
        const colors = {
            draft: "bg-secondary",
            active: "bg-primary",
            completed: "bg-success",
            archived: "bg-dark",
        };
        return <span className={`badge ${colors[status] || "bg-secondary"} ms-2`}>{status}</span>;
    };

    const getBuildStatusBadge = (buildStatus) => {
        const statusConfig = {
            pending: { class: 'bg-secondary', label: 'Pending' },
            building: { class: 'bg-info', label: 'Building' },
            ready: { class: 'bg-success', label: 'Ready' },
            failed: { class: 'bg-danger', label: 'Failed' },
        };
        const config = statusConfig[buildStatus] || statusConfig.pending;
        return <span className={`badge ${config.class} ms-2`}>{config.label}</span>;
    };

    return (
        <div className="modal-backdrop-custom" onClick={handleBackdropClick}>
            <div className="modal-content-custom" style={{ maxWidth: "600px" }}>
                <div className="modal-header-custom">
                    <h3>Assign Tool to Studies</h3>
                    <button className="modal-close-btn" onClick={onClose}>
                        &times;
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body-custom">
                        <div className="d-flex align-items-center gap-2 mb-3 p-2 bg-light rounded">
                            <i
                                className={`fa ${tool.icon || 'fa-wrench'}`}
                                style={{ color: tool.color || '#6c757d', fontSize: '1.5rem' }}
                            ></i>
                            <div>
                                <strong>{tool.name}</strong>
                                {getBuildStatusBadge(tool.build_status)}
                                <small className="d-block text-muted">
                                    {tool.model_type_display || tool.model_type}
                                </small>
                            </div>
                        </div>

                        <p className="text-muted mb-3">
                            Select which studies should have access to this tool
                        </p>

                        {studies.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                                <i className="fa fa-folder-open mb-2" style={{ fontSize: "2rem", opacity: 0.5 }}></i>
                                <p>No studies in this project yet.</p>
                                <small>Create a study first before assigning tools.</small>
                            </div>
                        ) : (
                            <div className="study-checkbox-list">
                                {studies.map((study) => (
                                    <div
                                        key={study.id}
                                        className={`study-checkbox-item ${selectedStudies.includes(study.id) ? "selected" : ""}`}
                                        onClick={() => handleCheckboxChange(study.id)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedStudies.includes(study.id)}
                                            onChange={() => handleCheckboxChange(study.id)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <div className="study-checkbox-info">
                                            <div className="study-checkbox-name">
                                                {study.name}
                                                {getStatusBadge(study.status)}
                                            </div>
                                            <div className="study-checkbox-meta">
                                                {study.description || "No description"}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {selectedStudies.length > 0 && (
                            <div className="mt-3 p-3 bg-light rounded">
                                <small className="text-muted">
                                    <i className="fa fa-check-circle me-2 text-success"></i>
                                    {selectedStudies.length} study(ies) selected
                                </small>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer-custom">
                        <button
                            type="button"
                            className="btn-secondary-custom"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-primary-custom"
                            disabled={loading || studies.length === 0}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Assigning...
                                </>
                            ) : (
                                `Assign to ${selectedStudies.length || 0} Study(ies)`
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AssignToolToStudyModal;
